import type { ComponentType, ReactNode } from 'react';
import { CloseButton } from './RoundIconButton';

type LinkProps = {
  to: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
};

type NavItem = {
  to?: string;
  href?: string;
  label: string;
  isActive?: boolean;
};

type MobileSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  logo?: ReactNode;
  items: NavItem[];
  footer?: ReactNode;
  children?: ReactNode;
  linkComponent?: ComponentType<LinkProps> | ((props: LinkProps) => ReactNode);
};

const getItemClass = ({ isActive }: { isActive?: boolean }) =>
  isActive
    ? 'rounded-xl px-4 py-3 text-sm font-semibold transition bg-primary/10 text-primary'
    : 'rounded-xl px-4 py-3 text-sm font-semibold transition text-slate-700 hover:bg-slate-50';

export const MobileSidebar = ({ isOpen, onClose, logo, items, footer, children, linkComponent: Link }: MobileSidebarProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] lg:hidden">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="absolute right-0 top-0 flex h-full w-72 flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          {logo}
          <CloseButton onClick={onClose} aria-label="Fechar menu" />
        </div>

        {children && <div className="border-b border-slate-100 px-3 py-3">{children}</div>}

        <nav className="flex flex-col gap-1 px-3 py-4">
          {items.map((item) => {
            const className = getItemClass({ isActive: item.isActive });

            if (item.to && Link) {
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={className}
                >
                  {item.label}
                </Link>
              );
            }

            return (
              <a
                key={item.href ?? item.to}
                href={item.href ?? item.to}
                onClick={onClose}
                className={className}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        {footer && <div className="mt-auto border-t border-slate-100 px-4 pt-2 pb-6">{footer}</div>}
      </aside>
    </div>
  );
};
